/**
 * CookieConsent — the ecosystem's ONE consent popup (lives in Core, the gateway)
 * ─────────────────────────────────────────────────────────────────────────────
 * A real modal popup in CoreMark glass language — not a banner.
 *
 * COMPLIANCE (Kinderdaten!):
 *   · DSGVO Art. 7 + öst. DSG: "Alle akzeptieren" is the highlighted hero
 *     (allowed), but a genuine same-layer decline ("Nur notwendige") exists —
 *     without it the consent of minors would be legally void (dark pattern).
 *   · Kid-readable language, no legalese wall.
 *   · Categories: Notwendig (locked ON) · Funktional · Statistik (anonym).
 *     StudoX has NO ad/tracking cookies — and says so.
 *   · Choice stored versioned + timestamped (studox-consent-v1); a floating
 *     🍪 chip re-opens settings anytime (revocability, Art. 7 Abs 3).
 */
import { useEffect, useState } from "react";

const KEY = "studox-consent-v1";

export function readConsent() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}
function writeConsent(categories) {
  localStorage.setItem(KEY, JSON.stringify({ v: 1, at: new Date().toISOString(), categories }));
}

const CATEGORIES = [
  { id: "necessary", locked: true, icon: "🔐", name: "Notwendig",
    desc: "Login, Sicherheit & Kinderschutz. Ohne diese funktioniert StudoX nicht — sie sind immer aktiv." },
  { id: "functional", locked: false, icon: "🎨", name: "Funktional",
    desc: "Merkt sich deine Einstellungen: Design, Sprache, zuletzt genutzte Apps. Für das beste Erlebnis." },
  { id: "statistics", locked: false, icon: "📊", name: "Statistik (anonym)",
    desc: "Anonyme Zahlen, damit wir StudoX besser machen können. Keine Profile, keine Namen, nichts Persönliches." },
];

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [toggles, setToggles] = useState({ necessary: true, functional: true, statistics: true });
  const [decided, setDecided] = useState(true); // assume decided until checked

  useEffect(() => {
    const existing = readConsent();
    if (!existing) { setDecided(false); setOpen(true); }
    else setToggles({ necessary: true, ...existing.categories });
  }, []);

  const closeWith = (categories) => {
    writeConsent(categories);
    setOpen(false);
    setDecided(true);
    setExpanded(false);
  };
  const acceptAll  = () => closeWith({ necessary: true, functional: true, statistics: true });
  const onlyNeeded = () => closeWith({ necessary: true, functional: false, statistics: false });
  const saveCustom = () => closeWith({ necessary: true, functional: !!toggles.functional, statistics: !!toggles.statistics });

  /* floating re-open chip (revocability) — only after a decision exists */
  if (!open) {
    if (!decided) return null;
    return (
      <button
        onClick={() => { setExpanded(false); setOpen(true); }}
        title="Cookie-Einstellungen"
        className="fixed bottom-3 left-3 z-[65] w-9 h-9 rounded-full grid place-items-center text-base transition-all hover:scale-110"
        style={{ background: "rgba(14,8,36,0.85)", border: "1px solid rgba(139,92,246,0.35)", backdropFilter: "blur(8px)", boxShadow: "0 4px 14px rgba(0,0,0,0.4)" }}
      >
        🍪
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center px-4"
      style={{ background: "rgba(2,0,12,0.78)", backdropFilter: "blur(14px)" }}>
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden animate-fade-in"
        style={{
          background: "linear-gradient(160deg, #17102f 0%, #0b0522 60%, #070315 100%)",
          border: "1.5px solid rgba(139,92,246,0.4)",
          boxShadow: "0 0 70px rgba(139,92,246,0.25), 0 40px 90px rgba(0,0,0,0.75)",
        }}
      >
        {/* header */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: "linear-gradient(90deg, transparent, #fbbf24, #8b5cf6, #22d3ee, transparent)" }} />
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl grid place-items-center text-3xl"
            style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.35)", boxShadow: "0 0 26px rgba(139,92,246,0.3)" }}>
            🛡️
          </div>
          <h2 className="text-lg font-black text-white">Deine Daten. Deine Kontrolle.</h2>
          <p className="mt-2 text-[13px] leading-relaxed text-white/65">
            StudoX nutzt Cookies, damit Login, Fortschritt und deine Einstellungen über alle
            StudoX-Apps hinweg funktionieren. <strong className="text-white/85">Damit StudoX perfekt läuft,
            empfehlen wir „Alle akzeptieren".</strong>
          </p>
          <div className="mt-3 px-3 py-2 rounded-xl text-[11px] leading-relaxed text-emerald-200/90"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
            💚 <strong>100% DSGVO- (EU) &amp; DSG-konform (Österreich).</strong> StudoX schützt Kinderdaten
            kompromisslos: keine Werbung, kein Verkauf von Daten, keine Tracker von Drittanbietern —
            alle Daten werden verschlüsselt in der EU verarbeitet.
          </div>
        </div>

        {/* categories (Anpassen) */}
        {expanded && (
          <div className="px-6 pb-2 space-y-2">
            {CATEGORIES.map((c) => {
              const on = c.locked || toggles[c.id];
              return (
                <button key={c.id} disabled={c.locked}
                  onClick={() => setToggles((t) => ({ ...t, [c.id]: !t[c.id] }))}
                  className="w-full flex items-start gap-3 p-3 rounded-2xl text-left transition-all"
                  style={{ background: "rgba(255,255,255,0.035)", border: `1px solid ${on ? "rgba(139,92,246,0.35)" : "rgba(255,255,255,0.08)"}` }}>
                  <span className="text-xl mt-0.5">{c.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center justify-between">
                      <span className="text-[13px] font-black text-white">{c.name}{c.locked && <span className="ml-2 text-[9px] font-bold text-emerald-400">IMMER AKTIV</span>}</span>
                      <span className="relative inline-block w-9 h-5 rounded-full transition-colors"
                        style={{ background: on ? "linear-gradient(90deg,#8b5cf6,#22d3ee)" : "rgba(255,255,255,0.12)", opacity: c.locked ? 0.65 : 1 }}>
                        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: on ? 18 : 2 }} />
                      </span>
                    </span>
                    <span className="block mt-1 text-[11px] leading-snug text-white/50">{c.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* actions */}
        <div className="px-6 pb-5 pt-3 space-y-2.5">
          <button onClick={acceptAll}
            className="relative w-full py-3.5 rounded-2xl font-black text-sm text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", boxShadow: "0 0 30px rgba(139,92,246,0.45), 0 8px 24px rgba(0,0,0,0.4)" }}>
            ✨ Alle akzeptieren
            <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[9px] font-black text-amber-950"
              style={{ background: "linear-gradient(90deg,#fde047,#fbbf24)", boxShadow: "0 2px 8px rgba(251,191,36,0.5)" }}>
              EMPFOHLEN
            </span>
          </button>

          {expanded ? (
            <button onClick={saveCustom}
              className="w-full py-3 rounded-2xl font-bold text-sm text-white/85 transition-all hover:scale-[1.01]"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(139,92,246,0.35)" }}>
              💾 Auswahl speichern
            </button>
          ) : (
            <button onClick={() => setExpanded(true)}
              className="w-full py-3 rounded-2xl font-bold text-sm text-white/85 transition-all hover:scale-[1.01]"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              ⚙️ Anpassen
            </button>
          )}

          {/* genuine same-layer decline — legally required for valid (child) consent */}
          <button onClick={onlyNeeded} className="w-full py-1.5 text-[11px] font-bold text-white/40 hover:text-white/70 transition-colors">
            Nur notwendige Cookies
          </button>

          <p className="text-center text-[9px] text-white/30 leading-relaxed">
            Du kannst deine Auswahl jederzeit über das 🍪-Symbol unten links ändern.
          </p>
        </div>
      </div>
    </div>
  );
}
